import { AfterViewInit, Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { AmplifyAuthenticatorModule, AuthenticatorService } from '@aws-amplify/ui-angular';
import { signIn, SignInInput } from 'aws-amplify/auth';
import { AuthService } from '@services/auth/auth.service';
import { TenantAuthentication, TenantAuthService } from '@services/auth/tenant-auth.service';
import { currentTenantSlug } from '@services/auth/tenant-routing';

@Component({
  selector: 'app-login',
  imports: [AmplifyAuthenticatorModule, ReactiveFormsModule, MatButtonModule],
  providers: [AuthenticatorService],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly tenantSlug = currentTenantSlug();
  protected ssoMode = false;
  protected busy = false;
  protected error = '';
  protected tenant?: TenantAuthentication;
  protected signedIn = false;
  protected readonly username = new FormControl('', {
    nonNullable: true, validators: [Validators.required, Validators.email]
  });
  protected readonly authenticator = inject(AuthenticatorService);
  protected readonly formFields = {
    signIn: {
      username: { label: 'Username', labelHidden: true, placeholder: 'Username', isRequired: true },
      password: { label: 'Password', labelHidden: true, placeholder: 'Password', isRequired: true }
    }
  };
  protected readonly services = {
    handleSignIn: (input: SignInInput): ReturnType<typeof signIn> => {
      this.auth.prepareSignIn(this.route.snapshot.queryParamMap.get('returnUrl'));
      this.completingLogin = true;
      // Amplify's Authenticator owns all challenges and subsequent sign-in steps.
      return signIn(input);
    }
  };
  private readonly auth = inject(AuthService);
  private readonly tenantAuth = inject(TenantAuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly zone = inject(NgZone);
  private subscription?: ReturnType<AuthenticatorService['subscribe']>;
  private completingLogin = false;
  private navigating = false;

  public async ngOnInit(): Promise<void> {
    if (this.route.snapshot.queryParamMap.has('error')) {
      this.error = 'SSO sign in was not completed. Please try again.';
    }
    if (this.route.snapshot.data['signOut']) { await this.signOut(); return; }
    await this.loadTenant();
  }

  public ngAfterViewInit(): void {
    this.subscription = this.authenticator.subscribe((state): void => {
      if (state?.route === 'authenticated' && this.completingLogin) {
        this.continueToApp();
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  protected showSso(show: boolean): void {
    this.ssoMode = show;
    this.error = '';
  }

  protected async signInWithSso(): Promise<void> {
    this.username.markAsTouched();
    if (this.username.invalid || this.busy) { return; }
    this.busy = true;
    this.error = '';
    try {
      await this.auth.signInWithSso(this.username.value, this.route.snapshot.queryParamMap.get('returnUrl'));
    } catch {
      this.busy = false;
      this.error = 'Unable to start SSO sign in. Check your corporate username and try again.';
    }
  }

  protected continueToApp(): void {
    if (this.navigating) { return; }
    this.navigating = true;
    // Reload to initialize permissions and discard the previous identity's caches.
    const requestedUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (requestedUrl && !this.completingLogin) { this.auth.prepareSignIn(requestedUrl); }
    const returnUrl = this.auth.consumeReturnUrl();
    this.navigateToApp(returnUrl);
  }

  protected navigateToApp(url: string): void {
    this.zone.run((): void => window.location.assign(url));
  }

  protected async signOut(): Promise<void> {
    this.busy = true;
    this.error = '';
    this.completingLogin = false;
    try {
      await this.auth.signOut();
      this.signedIn = false;
      this.username.reset();
      this.ssoMode = !this.tenant?.passwordEnabled;
    } catch {
      this.error = 'Unable to sign out. Please try again.';
    } finally {
      this.busy = false;
    }
  }

  private async loadTenant(): Promise<void> {
    if (!this.tenantSlug) { return; }
    this.busy = true;
    try {
      let tenant = this.tenantAuth.selected;
      if (tenant?.companyId.toLowerCase() !== this.tenantSlug) {
        tenant = await this.tenantAuth.discover(this.tenantSlug);
        this.tenantAuth.select(tenant);
      }
      this.tenant = tenant;
      this.ssoMode = !tenant.passwordEnabled;
      this.signedIn = await this.auth.isSignedIn();
      if (this.signedIn) { this.continueToApp(); }
    } catch {
      this.tenant = undefined;
      this.error = 'Sign-in is unavailable. Check your company link or try again later.';
    } finally {
      this.busy = false;
    }
  }
}
